import {
  FIXED_TOUR_ANCHORS,
  TOUR_SCROLL_SETTLE_INTERVAL,
  TOUR_SCROLL_SETTLE_TICKS,
  TOUR_SCROLL_SETTLE_TIMEOUT,
} from '../constants';
import type { TTourAnchorRect, TTourPlacement } from '../types';
import { measureTourAnchor } from './anchors';
import { traceTour } from './trace';
import type { TTourBounds } from './viewport';
import {
  getTourScrollHandle,
  maxTourSpotHeight,
  refreshTourViewport,
  tourSpotBounds,
  tourTooltipReserve,
} from './viewport';

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(() => resolve(), ms));

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

const scrollDelta = (
  rect: TTourAnchorRect,
  bounds: TTourBounds,
  placement: TTourPlacement,
): number => {
  const reserve = tourTooltipReserve(bounds);
  const visible = Math.min(rect.height, maxTourSpotHeight(bounds));

  const target =
    placement === 'top'
      ? clamp(rect.y, bounds.top + reserve, bounds.bottom - visible)
      : clamp(rect.y, bounds.top, bounds.bottom - reserve - visible);

  return rect.y - target;
};

const settle = async (name: string): Promise<TTourAnchorRect | null> => {
  let last: number | null = null;
  let stable = 0;

  for (
    let elapsed = 0;
    elapsed < TOUR_SCROLL_SETTLE_TIMEOUT;
    elapsed += TOUR_SCROLL_SETTLE_INTERVAL
  ) {
    await wait(TOUR_SCROLL_SETTLE_INTERVAL);

    const rect = await measureTourAnchor(name);

    if (!rect) {
      return null;
    }

    stable = last !== null && Math.abs(rect.y - last) < 1 ? stable + 1 : 0;
    last = rect.y;

    if (stable >= TOUR_SCROLL_SETTLE_TICKS) {
      return rect;
    }
  }

  traceTour(name, 'never settled, measuring it where it is');

  return measureTourAnchor(name);
};

export const prepareTourAnchor = async (
  name: string,
  placement: TTourPlacement,
): Promise<void> => {
  await refreshTourViewport();

  const handle = getTourScrollHandle();

  if (!handle || FIXED_TOUR_ANCHORS.includes(name)) {
    return;
  }

  const rect = await measureTourAnchor(name);

  if (!rect) {
    return;
  }

  const delta = scrollDelta(rect, tourSpotBounds(name), placement);

  if (Math.abs(delta) < 1) {
    return;
  }

  handle.scrollBy(delta);

  const settled = await settle(name);

  if (settled && Math.abs(scrollDelta(settled, tourSpotBounds(name), placement)) >= 1) {
    traceTour(name, 'stopped short of its target — the list ran out of scroll');
  }
};
